/* Badges: always text + icon (never color only) */
.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding-block: 2px;
  padding-inline: var(--space-2);
  border: var(--border-width) solid transparent;
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  line-height: 1.5;
  white-space: nowrap;
}

.badge svg {
  flex: none;
}

.neutral {
  background: var(--color-neutral-bg);
  border-color: var(--color-neutral-border);
  color: var(--color-neutral-text);
}
.primary {
  background: var(--color-primary-subtle);
  border-color: color-mix(in srgb, var(--color-primary) 30%, transparent);
  color: var(--color-primary-subtle-text);
}
.success {
  background: var(--color-success-bg);
  border-color: var(--color-success-border);
  color: var(--color-success-text);
}
.warning {
  background: var(--color-warning-bg);
  border-color: var(--color-warning-border);
  color: var(--color-warning-text);
}
.danger {
  background: var(--color-danger-bg);
  border-color: var(--color-danger-border);
  color: var(--color-danger-text);
}
.info {
  background: var(--color-info-bg);
  border-color: var(--color-info-border);
  color: var(--color-info-text);
}
.accent {
  background: var(--color-accent-subtle);
  border-color: color-mix(in srgb, var(--color-accent) 45%, transparent);
  color: var(--color-accent-text);
}

/* Callouts (inline notices) */
.callout {
  display: flex;
  gap: var(--space-3);
  align-items: flex-start;
  padding: var(--space-3) var(--space-4);
  border: var(--border-width) solid;
  border-inline-start-width: 4px;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
}

.callout > svg {
  flex: none;
  margin-block-start: 0.2em;
}

.calloutBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-inline-size: 0;
}

.calloutTitle {
  font-weight: var(--weight-semibold);
}

/* Error summary */
.summary {
  padding: var(--space-4);
  border: var(--border-width) solid var(--color-danger-border);
  border-inline-start: 4px solid var(--color-danger);
  border-radius: var(--radius-md);
  background: var(--color-danger-bg);
  color: var(--color-danger-text);
}

.summary:focus {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 2px;
}

.summaryTitle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
  color: inherit;
}

.summaryList {
  margin: var(--space-2) 0 0;
  padding-inline-start: var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.summaryLink {
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: start;
  text-decoration: underline;
  text-underline-offset: 0.2em;
  cursor: pointer;
}

.summaryField {
  font-weight: var(--weight-semibold);
}

/* Empty states */
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-10) var(--space-6);
  text-align: center;
}

.emptyIcon {
  display: grid;
  place-items: center;
  inline-size: 3rem;
  block-size: 3rem;
  border-radius: var(--radius-lg);
  background: var(--color-accent-subtle);
  color: var(--color-accent-text);
}

.emptyTitle {
  font-size: var(--text-lg);
}

.emptyText {
  max-inline-size: 32rem;
  color: var(--color-text-muted);
}

/* Skeletons */
.skeleton {
  display: block;
  border-radius: var(--radius-sm);
  background: linear-gradient(
    90deg,
    var(--skeleton-base) 0%,
    var(--skeleton-shine) 50%,
    var(--skeleton-base) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}

@keyframes shimmer {
  from {
    background-position: 100% 0;
  }
  to {
    background-position: -100% 0;
  }
}

/* Toasts */
.toastRegion {
  position: fixed;
  inset-block-end: calc(var(--docked-size, 0px) + var(--space-4));
  inset-inline-end: var(--space-4);
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  inline-size: min(26rem, calc(100vw - 2 * var(--space-4)));
  pointer-events: none;
}

.toastStack {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.toast {
  display: flex;
  gap: var(--space-3);
  align-items: flex-start;
  padding: var(--space-3) var(--space-3) var(--space-3) var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-inline-start: 4px solid var(--color-primary);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
  pointer-events: auto;
  animation: toast-in var(--duration-base) var(--ease-out);
}

.toast[data-tone='error'] {
  border-inline-start-color: var(--color-danger);
}

.toast[data-tone='error'] > svg {
  color: var(--color-danger-text);
}

.toast[data-tone='success'] > svg {
  color: var(--color-success-text);
}

.toast > svg {
  flex: none;
  margin-block-start: 0.15em;
}

.toastBody {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-inline-size: 0;
}

.toastTitle {
  font-weight: var(--weight-semibold);
}

.toastMeta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.toastMeta code {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  direction: ltr;
  unicode-bidi: isolate;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}

/* Modal dialogs */
.overlay {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: grid;
  place-items: start center;
  padding: max(8vh, var(--space-6)) var(--space-4) var(--space-4);
  background: var(--overlay);
  overflow-y: auto;
}

.overlay[data-entering] {
  animation: fade-in var(--duration-base) var(--ease-out);
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
}

.modal {
  inline-size: min(32rem, 100%);
  max-block-size: 92dvh;
  overflow: auto;
  background: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  outline: none;
}

.modalWide {
  inline-size: min(44rem, 100%);
}

.dialog {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-6);
  outline: none;
}

.dialogTitle {
  font-size: var(--text-lg);
}

.dialogBody {
  color: var(--color-text-muted);
}

.dialogActions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-2);
}
